import React, { type FC, type ReactNode } from 'react';
import { useIntl, defineMessages, FormattedDate, FormattedTime } from 'react-intl-next';

import { N500 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { Inline, Pressable, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';

import type { ContentMode } from '@confluence/content-types-utils';
import type { ShareContentType } from '@confluence/share';
import { CONTEXT_PATH } from '@confluence/named-routes';
import { Link } from '@confluence/route-manager';
import { useSidePanelStateActions } from '@confluence/side-panel-state-container';

import { lastEditedFriendlyFormatter } from '../LastEditedUtils';

const i18n = defineMessages({
	edited: {
		id: 'content-types-header.last-edited.edited',
		defaultMessage: 'Edited {timeAgo}',
		description:
			'Timestamp string that indicates the last time content was edited, (timeAgo) is of the form `1 hour ago` or a date `September 15, 2020`',
	},
	published: {
		id: 'content-types-header.last-edited.published',
		defaultMessage: 'Published {timeAgo}',
		description:
			'Timestamp string that indicates the last time content was published, (timeAgo) is of the form `1 hour ago` or a date `September 15, 2020`',
	},
});

const lastEditedStyles = xcss({
	color: 'color.text.subtle',
	alignItems: 'center',
	flexDirection: 'row',
	padding: 'space.050',
	textWrap: 'nowrap',
});

const linkStyles = {
	color: token('color.text.subtle', N500),
};

const pressableStyles = xcss({
	color: 'color.text.subtle',
	font: token('font.body'),
	':hover': {
		textDecoration: 'underline',
	},
});

export type LastEditedUserShape = {
	isAnonymous: boolean;
	userId: string;
	fullName: string;
	avatarUrl: string;
};

export type LastEditedVersion = {
	by?: LastEditedUserShape;
	contentTypeModified: boolean;
	number: number;
	when: string;
};

export type LastEditedProps = {
	contentId: string;
	version: LastEditedVersion;
	contentType: ShareContentType;
	contentMode: ContentMode | undefined;
	spaceKey: string;
};

export const LastEdited: FC<LastEditedProps> = ({
	contentId,
	version,
	contentType,
	contentMode,
	spaceKey,
}) => {
	const { showSidePanel } = useSidePanelStateActions();
	const { formatMessage, formatDate } = useIntl();

	const createEditorChangeLink = (formattedMessage: string) => {
		return (
			<Link
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={linkStyles}
				href={`${CONTEXT_PATH}/spaces/${spaceKey}/history/${contentId}`}
			>
				{formattedMessage}
			</Link>
		);
	};

	const handleContentTypesLinkClick = () => {
		showSidePanel();
	};

	const createContentTypesChangeLink = (formattedMessage: string) => {
		return (
			<Pressable
				xcss={pressableStyles}
				backgroundColor="color.background.neutral.subtle"
				onClick={handleContentTypesLinkClick}
			>
				{formattedMessage}
			</Pressable>
		);
	};

	let lastEditedMessage: ReactNode;
	const isPageOrBlog = contentType === 'page' || contentType === 'blogpost';
	const lastEditedTime = new Date(version.when);
	const friendlyWhen = lastEditedFriendlyFormatter(lastEditedTime, formatMessage, formatDate);

	if (isPageOrBlog) {
		const hasEditorVersionHistory =
			version.number > 1 && !version.contentTypeModified && spaceKey && contentMode !== 'draft';

		if (contentMode === 'view' || contentMode === 'archived') {
			const publishText = formatMessage(i18n.published, { timeAgo: friendlyWhen });
			lastEditedMessage = hasEditorVersionHistory
				? createEditorChangeLink(publishText)
				: publishText;
		} else {
			const editText = formatMessage(i18n.edited, { timeAgo: friendlyWhen });
			lastEditedMessage = hasEditorVersionHistory ? createEditorChangeLink(editText) : editText;
		}
	} else if (contentType === 'whiteboard' || contentType === 'database') {
		// contentTypes
		const hasContentTypesVersionHistory =
			version.number > 1 && !version.contentTypeModified && contentType === 'whiteboard';
		const editText = formatMessage(i18n.edited, { timeAgo: friendlyWhen });

		lastEditedMessage = hasContentTypesVersionHistory
			? createContentTypesChangeLink(editText)
			: editText;
	} else {
		return null;
	}

	const fullFormattedDateTime = (
		<span>
			<FormattedDate value={version.when} day="numeric" month="short" year="numeric" />{' '}
			<FormattedTime value={version.when} hour="2-digit" minute="numeric" />
		</span>
	);

	return (
		<Tooltip content={fullFormattedDateTime}>
			<Inline xcss={lastEditedStyles}>{lastEditedMessage}</Inline>
		</Tooltip>
	);
};
